import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { ActionItem, User, Metric } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatDateForInput } from '../../../../utils/date.utils';

interface EditActionItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: any) => Promise<void>;
  editingActionItem: ActionItem | null;
  users?: User[]; // Optional - component now uses eosUsers from AuthContext
  rocks?: Array<{ id: string; rock: string }>;
  issues?: Array<{ id: string; name: string }>;
  metrics?: Metric[];
}

/**
 * Edit Action Item Modal Component
 * Uses Formik for form state and Yup for validation
 * Migrated from Tailwind to Chakra UI
 */
export function EditActionItemModal({
  isOpen,
  onClose,
  onSave,
  editingActionItem,
  users,
  rocks = [],
  issues = [],
  metrics = [],
}: EditActionItemModalProps) {
  const { eosUsers = [] } = useAuth();
  // Validation schema using Yup
  const validationSchema = Yup.object({
    description: Yup.string()
      .required('Description is required')
      .min(10, 'Description must be at least 10 characters')
      .max(500, 'Description must be at most 500 characters')
      .trim(),
    dueDate: Yup.date().required('Due date is required'),
    ownerId: Yup.string().required('Owner is required'),
    assignedToUserId: Yup.string().nullable(),
    linkedRockId: Yup.string().nullable(),
    linkedIssueId: Yup.string().nullable(),
    linkedMetricId: Yup.string().nullable(),
  });



  const initialValues = {
    description: editingActionItem?.description || '',
    dueDate: editingActionItem?.dueDate ? formatDateForInput(editingActionItem.dueDate) : '',
    ownerId: editingActionItem?.ownerId || '',
    assignedToUserId: editingActionItem?.assignedToUserId || null,
    linkedRockId: editingActionItem?.linkedRockId || null,
    linkedIssueId: editingActionItem?.linkedIssueId || null,
    linkedMetricId: editingActionItem?.linkedMetricId || null,
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      console.error('Error updating action item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!editingActionItem) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Action Item</ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Field name="description">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched}>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Describe the action item (10-500 characters)..."
                          rows={4}
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="dueDate">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched}>
                        <FormLabel>Due Date</FormLabel>
                        <Input {...field} type="date" />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="ownerId">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched}>
                        <FormLabel>Owner</FormLabel>
                        <Select {...field}>
                          <option value="">Select owner...</option>
                          {eosUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="assignedToUserId">
                    {({ field }: any) => (
                      <FormControl>
                        <FormLabel>Assign To (Optional)</FormLabel>
                        <Select {...field} value={field.value || ''}>
                          <option value="">None</option>
                          {eosUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>

                  {rocks.length > 0 && (
                    <Field name="linkedRockId">
                      {({ field }: any) => (
                        <FormControl>
                          <FormLabel>Link to Rock (Optional)</FormLabel>
                          <Select {...field} value={field.value || ''}>
                            <option value="">None</option>
                            {rocks.map((rock) => (
                              <option key={rock.id} value={rock.id}>
                                {rock.rock}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {issues.length > 0 && (
                    <Field name="linkedIssueId">
                      {({ field }: any) => (
                        <FormControl>
                          <FormLabel>Link to Issue (Optional)</FormLabel>
                          <Select {...field} value={field.value || ''}>
                            <option value="">None</option>
                            {issues.map((issue) => (
                              <option key={issue.id} value={issue.id}>
                                {issue.name}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {metrics.length > 0 && (
                    <Field name="linkedMetricId">
                      {({ field }: any) => (
                        <FormControl>
                          <FormLabel>Link to Metric (Optional)</FormLabel>
                          <Select {...field} value={field.value || ''}>
                            <option value="">None</option>
                            {metrics.map((metric) => (
                              <option key={metric.id} value={metric.id}>
                                {metric.metricName || 'Unknown Metric'}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Field>
                  )}
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Update Action Item
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
